import {makeAutoObservable, runInAction} from "mobx";
import SubscriptionPlansService, {IPlanResponse} from "../../../services/api/subscription-plans-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import {IContactUs, ICurrentUserPlan, IPlanFeature, IPlan, PLAN_TYPES, FEATURES } from "../../../models/Plan";
import {HTMLAttributeAnchorTarget} from "react";
import config from "../../../config";
import {IIframeService} from "../../../services/iframe-service/iframe-service";
import {AddonStore} from "../../accounts/addons-section/stores/addons-store-list";
import {PLAN_CHARGE_TYPES} from "../../manage-subscription-plans/stores/subscription-plan-editor-store";

export interface IPlanButton {
    target: HTMLAttributeAnchorTarget;
    caption: string;
    isCurrent: boolean;
    href: string;
}

function alignIPlanFeatureTypes(data: IPlanFeature) {
    const toNum = (v: undefined | any) => v !== undefined ? Number(v) : v;
    const toBool = (v: undefined | any) => Boolean(v);

    return {
        ...data,
        feature_id: toNum(data.feature_id),
        is_quota_based: toBool(data.is_quota_based),
        quota: toNum(data.quota)
    }
}

function alignIPlanTypes(plan: IPlan): IPlan {
    let parentPlan = null;
    if (plan.parent_plan) {
        parentPlan = {
            ...plan.parent_plan,
            features: plan.parent_plan?.features?.map(f => alignIPlanFeatureTypes(f)) ?? []
        }
    }
    return {
        ...plan,
        parent_plan_features: plan.parent_plan_features?.map(f => alignIPlanFeatureTypes(f)) ?? [],
        plan_features_with_addons: plan.plan_features_with_addons?.map(f => alignIPlanFeatureTypes(f)) ?? [],
        features: plan.features?.map(f => alignIPlanFeatureTypes(f)) ?? [],
        parent_plan: parentPlan as IPlan
    };
}


export class PlanStore {
    private readonly _model: IPlan;
    private readonly _user_addons: AddonStore[];
    private _shouldRenderLongDescription: boolean = false;

    constructor(plan: IPlan) {
        this._model = alignIPlanTypes(plan);
        this._user_addons = plan.user_addons?.map(addon => new AddonStore(addon)) ?? [];
    }

    public get id() { return this._model?.id ?? 0; }
    public get name() { return this._model?.name ?? ''; }
    public get internal_name() { return this._model?.internal_name ?? ''; }
    public get price_per_extra_rma() { return this._model?.price_per_extra_rma ?? 0; }
    public get price() { return this._model?.price ?? 0; }
    public get parent_plan_id() { return this._model?.parent_plan_id ?? 0; }
    public get rma_quota() { return this._model?.rma_quota; }
    public get parentPlan() {return this._model?.parent_plan; }
    public get tag() { return this._model?.tag ?? ''; }
    public get tag_color() { return this._model?.tag_color ?? ''; }
    public get plan_description() { return this._model?.plan_description ?? ''; }
    public get is_custom() { return this._model?.is_custom; }
    public get deprecated() { return this._model?.deprecated; }
    public get capped_amount() { return this._model?.capped_amount; }
    public get updated_at() { return this._model?.updated_at; }
    public get trial_days() { return this._model?.trial_days; }
    public get plan_type() { return this._model?.plan_type; }
    public get short_description() { return this._model?.short_description; }
    public get parentPlanPrice() { return this._model?.parent_plan?.price ?? 0; }
    public get partnerId() { return this._model?.partner_id; }
    public get period() { return this._model?.period; }
    public get type() { return this._model?.type; }
    public get installments() { return this._model?.installments; }
    public get shouldRenderLongDescription() { return this._shouldRenderLongDescription; }

    public setShouldRenderLongDescription(value: boolean) {
        this._shouldRenderLongDescription = value;
    }

    public hasInstallments(): boolean {
        if(this.installments && this.installments.length > 0) {
            return true;
        }

        if(this.parentPlan && this.parentPlan.installments && this.parentPlan.installments?.length > 0) {
            return true;
        }

        return false;
    }
    public isAnnualPlan(): boolean {
        if(this.type === PLAN_CHARGE_TYPES.ANNUAL) {
            return true;
        }

        if(this.parentPlan?.type === PLAN_CHARGE_TYPES.ANNUAL) {
            return true;
        }

        return false;
    }
    public get formattedTotalAnnualPlanPrice() {
        if(!this.isAnnualPlan()) {
            return 0;
        }

        if(!this.parentPlan) {
            return 0;
        }

        return (this.parentPlan.price + (this.priceWithAddons * 12)).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2
        }).toString();
    }

    /**TO BE DEPRECATED RD-478 should be replaced with this.addons */
    public get userAddons() { return this._user_addons; }

    /**TO BE DEPRECATED RD-478 should be replaced using this._plan.price */
    public get priceWithAddons() { return this._model?.price_with_addons ?? 0; }

    public get formattedPriceWithAddons() {
        return this.priceWithAddons.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2
        }).toString();
    }

    public get formattedPrice() {
        return this.price.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2
        }).toString();
    }

    public get formattedBasePrice() {
        const price = this.parentPlan ? this.parentPlan.price : this.price;
        return price.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2
        }).toString();
    }

    public get formattedPricePerExtraRma() {
        return parseFloat((this.price_per_extra_rma ?? 0).toString()).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        });
    }

    public get formattedRmaQuota() {
        return this.rma_quota
            ? parseInt(this.rma_quota).toLocaleString('en-US')
            : 'Unlimited';
    }

    public get isDefaultCoveragePlan(): boolean {
        return this._model.is_default_coverage_plan;
    }

    public isFeatureIncluded(featureId: FEATURES): boolean {
        return this._model.features.some(feature => feature.feature_id === featureId);
    }

    public getModel() {
        return this._model;
    }

    /** returns quota from parent plan - the quota from original plan if exists */
    public getQuotaFromPlan(feature_id: number): number | undefined | null {
        let foundFeature;
        if (this.plan_type === PLAN_TYPES.SYSTEM) {
            foundFeature = this._model.parent_plan?.features?.find((feature: IPlanFeature) => {
                return feature.feature_id === feature_id;
            });
        } else {
            foundFeature = this._model.features?.find((feature: IPlanFeature) => {
                return feature.feature_id === feature_id;
            });
        }

        if (foundFeature) {
            return foundFeature.quota;
        }

        return undefined;
    }

    /** returns quota from bought addon if exists */
    public getQuotaFromAddon(feature_id: number): number | undefined | null {
        let foundFeature;
        if (this.plan_type === PLAN_TYPES.SYSTEM) {
            foundFeature = this._model.features?.find((feature: IPlanFeature) => {
                return feature.feature_id === feature_id;
            });
        }

        if (foundFeature) {
            const originalQuota = this.getQuotaFromPlan(feature_id);
            if (originalQuota !== undefined) {
                return (foundFeature.quota ?? 0) - (originalQuota ?? 0);
            }
            return foundFeature.quota;
        }

        return undefined;
    }

}


class SubscriptionPlansStore {
    private _isLoadingPlans: boolean = false;
    private _isLoadingContactUs: boolean = false;
    private _isLoadingCurrent: boolean = false;
    private _showAddons: boolean = true;
    private _showMore: boolean = false;
    private _is_generating_link: boolean = false;
    private _isOnTrial: boolean = true;
    private _childStoresCount: number = 0;

    private _planList: PlanStore[] = [];
    private _userPlan: PlanStore | undefined;
    private _contactUs: IContactUs = {} as IContactUs;

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    constructor() {
        makeAutoObservable(this);
        this._showAddons = false;
    }

    public get planList() { return this._planList; }
    public get isOnTrial() { return this._isOnTrial; }
    public get childStoresCount() { return this._childStoresCount; }
    public get userPlan() { return this._userPlan; }
    public get userPlanUpdatedAt() { return this._userPlan?.updated_at; }
    public get trialDays() { return this._userPlan?.trial_days; }
    public get isLoading() { return this._isLoadingPlans || this._isLoadingCurrent || this._isLoadingContactUs; }
    public get contactUs(): IContactUs { return this._contactUs; }
    public get showMore() { return this._showMore; }
    private get subscriptionPlansService(): SubscriptionPlansService {
        return rootContainer.get<SubscriptionPlansService>(TYPE.SubscriptionPlansService);
    }

    public set showMore(showMore: boolean) { this._showMore = showMore; }
    public set showAddons(showAddons: boolean) {
        if (!showAddons) {
            this.showMore = true;
        }
        this._showAddons = showAddons;
    }

    async generateSystemPlan(newPlan: PlanStore) {
        /** this._is_generating_link prevents from posting another request while being processing */
        if (this._is_generating_link) {
            return false;
        }
        this._is_generating_link = true;
        let errored = false;

        if (newPlan) {
            let generateBillingLink: IPlanResponse|undefined;

            try {
                generateBillingLink = await this.subscriptionPlansService.generateBillingLink(newPlan.getModel());
            } 
            catch {
                errored = true;
            }

            runInAction(() => {
                if (generateBillingLink?.redirect_to) {
                    this.iframeService.redirectParentFrame(generateBillingLink.redirect_to);
                } 
                else {
                    if (!errored) {
                        /** when valid response and no redirect link - this can happen only when trying to move to cheaper annual plan */
                        alert("moving to cheaper annual plan is not supported");
                    }
                }
                this._is_generating_link = false;
            });
        } 
        else {
            this._is_generating_link = false;
        }
    }

    public getButtonData(plan: PlanStore): IPlanButton {
        const href = `${config.apiUrl}/billing/${plan.id}`;
        if (this._userPlan) {
            /** when plan is not one of the oferred use 'select' for all plans captions */
            let caption = 'select';

            if (!this._userPlan.is_custom && !this._userPlan.deprecated) {
                /** when plan is one of the oferred use those captions */
                if (plan.price > this._userPlan.price) {
                    caption = 'upgrade now';
                }
            }

            return {
                isCurrent: plan.id === this._userPlan.id || plan.id === this._userPlan.parent_plan_id,
                caption,
                target: '_blank',
                href,
            };
        }

        return {
            isCurrent: false,
            caption: 'start free trial',
            target: '_self',
            href,
        };
    }

    private async _loadPlans() {
        this._isLoadingPlans = true;
        const res = await this.subscriptionPlansService.getPlans();
        runInAction(() => {
            this._planList = res.payload
                .filter(plan => plan.type === PLAN_CHARGE_TYPES.RECURRING)
                .map(plan => new PlanStore(plan))
                .sort((s1, s2) => s1.price - s2.price); /** sorting by cheapest (on the left) to most expansive */
            this._isLoadingPlans = false;
        });
    }

    private async _loadCurrent() {
        this._isLoadingCurrent = true;
        let res: ICurrentUserPlan;
        try {
            res = await this.subscriptionPlansService.getCurrentUserPlan();
        } catch {
        }
        runInAction(() => {
            if (res) {
                if (res.plan) {
                    this._userPlan = new PlanStore(res.plan);
                    if (this._userPlan.userAddons.length === 0) {
                        this.showMore = true;
                    }
                } else {
                    this.showMore = true;
                }
                this._isOnTrial = res.isOnTrial;
                this._childStoresCount = res.childStoresCount;
            }
            this._isLoadingCurrent = false;
        });
    }

    private async _loadContactUs() {
        this._isLoadingContactUs = true;
        let res: IContactUs;
        try {
            res = await this.subscriptionPlansService.getContactUs();
        } catch {
        }
        runInAction(() => {
            if (res) {
                this._contactUs = res;
            }
            this._isLoadingContactUs = false;
        });
    }

    async load() {
        await Promise.all([
            this._loadPlans(),
            this._loadCurrent(),
            this._loadContactUs(),
        ])
    }

}

export default new SubscriptionPlansStore();
